import { useReportingTimeZone } from '@/hooks/use-reporting-timezone';
import { SupernovaDatePicker, civilDateToInstant, instantToCivilDate, layoutClassesOnly } from './date-picker';

interface MonthInputProps {
  /** 'YYYY-MM' (HTML month format — preserved so form/API logic is unaffected). */
  value?: string;
  /** Synthetic `{ target: { value } }` so existing `(e) => setX(e.target.value)`
   *  handlers keep working unchanged. */
  onChange?: (e: { target: { value: string } }) => void;
  /** 'YYYY-MM' lower / upper bounds (earlier/later months are disabled). */
  min?: string;
  max?: string;
  disabled?: boolean;
  className?: string;
}

/** Last calendar day of a 'YYYY-MM' month, as 'YYYY-MM-DD'. */
function lastDayOf(month: string): string {
  const [y, m] = month.split('-').map(Number);
  return `${month}-${String(new Date(y, m, 0).getDate()).padStart(2, '0')}`;
}

/**
 * MonthInput — a month field built on the app-standard SupernovaDatePicker,
 * preserving the legacy string 'YYYY-MM' value + synthetic onChange API. Follows
 * the active location zone (the month is resolved via its first day in that
 * zone, both ways, so it can't slip into an adjacent month).
 */
export function MonthInput({ value, onChange, min, max, disabled, className }: MonthInputProps) {
  const { timeZone } = useReportingTimeZone();
  return (
    <SupernovaDatePicker
      mode="month"
      timezone={timeZone}
      presets={false}
      disabled={disabled}
      // Legacy callers pass the old native-input skin; only its layout survives
      // (the picker draws its own bordered input — see layoutClassesOnly).
      className={layoutClassesOnly(className)}
      value={value ? civilDateToInstant(`${value}-01`, timeZone) : null}
      // Bounds span whole months: from the first day of `min` to the last day of `max`.
      minDate={min ? (civilDateToInstant(`${min}-01`, timeZone) ?? undefined) : undefined}
      maxDate={max ? (civilDateToInstant(lastDayOf(max), timeZone) ?? undefined) : undefined}
      onChange={(v) => {
        const s = instantToCivilDate(v as Date | null, timeZone);
        onChange?.({ target: { value: s ? s.slice(0, 7) : '' } });
      }}
    />
  );
}
